import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary компонент для обработки ошибок React
 * Перехватывает ошибки в дочерних компонентах и отображает fallback UI
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        // Обновляем состояние, чтобы следующий рендер показал fallback UI
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Логируем ошибку в консоль и в систему мониторинга (если есть)
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error,
            errorInfo,
        });

        // Здесь можно отправить ошибку в сервис мониторинга (например, Sentry)
        // logErrorToService(error, errorInfo);
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            // Если передан кастомный fallback, используем его
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Стандартный fallback UI
            return (
                <div className="error-boundary" role="alert" aria-live="assertive">
                    <div className="error-boundary-content">
                        <h1>Что-то пошло не так</h1>
                        <p>Произошла непредвиденная ошибка. Пожалуйста, попробуйте обновить страницу.</p>
                        
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="error-details">
                                <summary>Детали ошибки (только в режиме разработки)</summary>
                                <pre className="error-message">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo && (
                                        <>
                                            {'\n\n'}
                                            {this.state.errorInfo.componentStack}
                                        </>
                                    )}
                                </pre>
                            </details>
                        )}
                        
                        <div className="error-actions">
                            <button 
                                onClick={this.handleReset}
                                className="error-retry-button"
                                aria-label="Попробовать снова"
                            >
                                Попробовать снова
                            </button>
                            <button 
                                onClick={() => window.location.reload()}
                                className="error-reload-button"
                                aria-label="Обновить страницу"
                            >
                                Обновить страницу
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
